import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { ColorStyle } from "src/utils/Colors";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { useMutation } from "@apollo/client";
import { ums_customersettings_insert_input } from "src/api/generated/globalTypes";
import { Route } from "src/config/routes";
import { useLocation } from "react-router-dom";
import styles from "./TicketDemoModeOrArchivedBanner.module.css";
import { UpsertCustomersetting, UpsertCustomersettingVariables } from "src/api/generated/UpsertCustomersetting";
import { UPSERT_CUSTOMERSETTING } from "src/api/customer";
import { MessageType } from "src/components/notifications/Notifier";
import { Role } from "src/network/User";
import { useHistory } from "react-router-dom";
import { TICKET_STATE } from "src/stores/TicketStore";

const TicketDemoModeOrArchivedBannerBase = () => {
    const { t } = useTranslation();
    const { authStore, uiStore, navStore, ticketStore } = useContext(RootStoreContext);
    const location = useLocation();
    const history = useHistory();

    const [upsertCustomersettingMutation] = useMutation<UpsertCustomersetting, UpsertCustomersettingVariables>(
        UPSERT_CUSTOMERSETTING,
        {
            onCompleted: () => {
                uiStore.printStatusMessage(
                    `${t("screens.tickets.demo_mode.successfull_deactivaion")}`,
                    MessageType.SUCCESS
                );
                uiStore.setIsDemoModeTicketingActive(false);
                history.push(Route.ticketingsettings);
            }
        }
    );

    const onClickDeactivateDemoModeTicketing = useCallback(async () => {
        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            if (uiStore.isDemoModeTicketingActive) {
                try {
                    const insertInput: ums_customersettings_insert_input[] = [];
                    insertInput.push({
                        key: "TICKET.DEMOMODE",
                        value: "false",
                        valuetype: 3
                    });

                    await upsertCustomersettingMutation({
                        variables: {
                            objects: insertInput
                        }
                    });
                } catch (error: any) {
                    uiStore.setIsDemoModeTicketingActive(true);

                    uiStore.printStatusMessage(
                        `${t("screens.tickets.demo_mode.error_deactivation")}: ${error.message}`,
                        MessageType.ERROR
                    );
                }
            }
        } else {
            uiStore.printStatusMessage(
                `${t("screens.tickets.demo_mode.permission_admin")}`,
                MessageType.WARNING,
                10000
            );
        }
    }, [uiStore, upsertCustomersettingMutation, authStore.user?.availableRoles, t]);

    const shouldRenderTicketArchivedBanner =
        location.pathname.includes(Route.ticket) && ticketStore.currentTicket?.state === TICKET_STATE.ARCHIVED;
    const shouldRenderTicketDemoModeBanner =
        uiStore.isDemoModeTicketingActive &&
        (location.pathname.includes(Route.newTicket) ||
            location.pathname.includes(Route.tickets) ||
            location.pathname.includes(Route.ticket)) &&
        ticketStore.currentTicket?.state !== TICKET_STATE.ARCHIVED;

    if (
        authStore.user?.hasOnlyEcAdminRole ||
        !authStore.token ||
        (!shouldRenderTicketDemoModeBanner && !shouldRenderTicketArchivedBanner)
    ) {
        return null;
    }

    if (shouldRenderTicketDemoModeBanner) {
        return (
            <div className={`${styles.TicketDemoModeBanner} container-fluid`}>
                <UI.Row>
                    <UI.Col xl={9} sm={9} className="d-flex align-items-center mb-4 mb-lg-0">
                        <UI.Icon
                            className={styles.TicketDemoModeInfoIcon}
                            icon={UI.SVGIcon.Information}
                            color={ColorStyle("white")}
                        />
                        <div
                            className={styles.TicketDemoModeText}
                            dangerouslySetInnerHTML={{
                                __html: t("screens.tickets.demo_mode.info_text", {
                                    customerEmail: navStore.customer.email
                                }).toString()
                            }}
                        />
                    </UI.Col>
                    <UI.Col xl={3} sm={3} className="d-flex align-items-center justify-content-end">
                        <button onClick={onClickDeactivateDemoModeTicketing} className={styles.TicketDemoModeButton}>
                            {t("screens.tickets.demo_mode.button_deactivate")}
                        </button>
                    </UI.Col>
                </UI.Row>
            </div>
        );
    }

    if (shouldRenderTicketArchivedBanner) {
        return (
            <div className={`${styles.TicketArchivedBanner} container-fluid`}>
                <UI.Row>
                    <UI.Col xl={9} sm={9} className="d-flex align-items-center mb-4 mb-lg-0">
                        <UI.Icon
                            className={styles.TicketDemoModeInfoIcon}
                            icon={UI.SVGIcon.Information}
                            color={ColorStyle("white")}
                        />
                        <div className={styles.TicketDemoModeText}>
                            {t("screens.tickets.demo_mode.info_text_archived")}
                        </div>
                    </UI.Col>
                </UI.Row>
            </div>
        );
    }

    return null;
};

export const TicketDemoModeOrArchivedBanner = Sentry.withProfiler(observer(TicketDemoModeOrArchivedBannerBase));
